import * as React from 'react';
import { Provider } from 'react-redux';
import { IApplicationContext } from '../../../types/applicationContext';
import { react2angular } from 'react2angular';
import app from '../../../../browser/addform/scripts/app';
import { ApplicationContext } from '../../ApplicationContext';
import { IAppState } from '../../../redux';
import { Store } from 'redux';
import { lowerFirst } from '../../../utils/string';
import { Provider as MicrofrontendProvider } from '@cian/mf-react';
import { IConfig } from '@cian/config/shared';
import { ILogger } from '@cian/logger';
import { ITelemetryLogger } from '@cian/telemetry/shared';
import { IServicePickerWidgetMicrofrontendAPI } from '../../../types/microfrontends/servicePickerWidget';
import { IRegistry } from '@cian/mf-registry/shared';

interface IProps {
  logger: ILogger;
  config: IConfig;
  telemetry: ITelemetryLogger;
  store: Store<IAppState>;
  microfrontendsRegistry: IRegistry;
  servicePickerWidgetMicrofrontend?: IServicePickerWidgetMicrofrontendAPI;
}

/**
 * HOC подключает React-компонент к дереву зависимостей Angular, оборачивает в базовый контекст и подключает redux-store.
 * В качестве параметров нужно передать лишь компонент, остальное сделает метод.
 * Имя в Angular-дереве берётся из названия компонента.
 * После нужно сделать импорт в app-entry и добавить компонент в jade-шаблон в kebab-формате.
 * Например, имя компонента MyAwesomeComponent в шаблоне добавляется по директиве my-awesome-component.
 * Важно! Не забудьте указать у подключаемого компонента параметр displayName.
 *
 * Сейчас у нас компоненты создаются изолированно, поэтому необходимо каждый root-компонент
 * оборачивать в этот HOC. В будущем эта необходимость отпадёт после отказа от Ангуляра.
 */
export function attachReactComponent(Component: React.ComponentType) {
  const WrappedComponent = (props: IProps) => {
    const { store, config, logger, telemetry, microfrontendsRegistry, servicePickerWidgetMicrofrontend } = props;

    const context = React.useMemo(
      () => ({
        config,
        logger,
        telemetry,
        servicePickerWidgetMicrofrontend,
      }),
      [config, logger, telemetry, servicePickerWidgetMicrofrontend],
    ) as IApplicationContext;

    return (
      <Provider store={store}>
        <MicrofrontendProvider registry={microfrontendsRegistry}>
          <ApplicationContext.Provider value={context}>
            <Component />
          </ApplicationContext.Provider>
        </MicrofrontendProvider>
      </Provider>
    );
  };

  app.component(
    lowerFirst(Component.displayName!),
    react2angular(
      WrappedComponent,
      /**
       * Параметры, передаваемые через Jade, не инжектим намеренно!
       * Инжектинг параметров увеличивает легаси, способствует проникновению Ангуляра в Реакт.
       * Это недопустимо!
       */
      [],
      /**
       * Внимание!
       * Несмотря на наличие httpApi в интерфейсе IApplicationContext, здесь он не инжектится.
       * Также запрещено инжектить сервисы Ангуляра, дабы не размазывать легаси.
       * Это сделано намеренно.
       */
      ['config', 'logger', 'telemetry', 'store', 'microfrontendsRegistry', 'servicePickerWidgetMicrofrontend'],
    ),
  );
}
